import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  ActivityIndicator,
  Alert,
  ScrollView,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '../navigation/RootNavigator';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import Icon from 'react-native-vector-icons/Ionicons';
import { api } from '../services/api';
import logger from '../utils/logger';

interface DetectionResult {
  gateway: {
    id: string;
    name: string;
    sn: string;
    siteName: string;
    zoneName?: string;
  };
  sensor: {
    sensorNumber: string;
    serialNumber: string;
    hasTemperature: boolean;
    hasHumidity: boolean;
    hasBattery: boolean;
    hasSignal: boolean;
    totalDatapoints: number;
    suggestedName: string;
  };
  availableMetrics: string[];
  optionalMetrics: string[];
  datapoints: Record<
    string,
    {
      datapointId: number;
      symbol: string;
      unit: string;
      lastValue: number;
      lastStatus: string;
    }
  >;
  isDuplicate: boolean;
  existingDevice?: {
    id: string;
    name: string;
  };
}

type DetectionResultNavigationProp = StackNavigationProp<RootStackParamList, 'DetectionResult'>;
type DetectionResultRouteProp = RouteProp<RootStackParamList, 'DetectionResult'>;

export default function DetectionResultScreen() {
  const navigation = useNavigation<DetectionResultNavigationProp>();
  const route = useRoute<DetectionResultRouteProp>();
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const { serialNumber } = route.params;

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<DetectionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    detectSensor();
  }, []);

  const detectSensor = async () => {
    try {
      setLoading(true);
      setError(null);

      logger.log('🔍 Detecting sensor from serial:', serialNumber);

      const response = await api.post('/devices/detect-from-serial', {
        serialNumber,
      });

      if (response.data.success) {
        setResult(response.data);
        logger.log('✅ Detection successful:', response.data);
      } else {
        throw new Error(response.data.error?.message || t('detection_failed'));
      }
    } catch (err: any) {
      logger.error('Detection error:', err);

      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error?.messageAr ||
        err.message ||
        t('detection_failed');

      setError(errorMessage);

      Alert.alert(t('error'), errorMessage, [
        { text: t('try_again'), onPress: detectSensor },
        { text: t('cancel'), onPress: () => navigation.goBack() },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    if (!result) return;

    // Navigate to metric selection screen
    navigation.navigate('DeviceConfig', {
      serialNumber,
      sensor: result.sensor,
      detection: result,
      mode: 'scan' as const,
    });
  };

  const getMetricIcon = (metric: string) => {
    switch (metric) {
      case 'temperature':
        return '🌡️';
      case 'humidity':
        return '💧';
      case 'battery':
        return '🔋';
      case 'rssi':
        return '📶';
      default:
        return '📊';
    }
  };

  const getMetricName = (metric: string) => {
    switch (metric) {
      case 'temperature':
        return isRTL ? 'درجة الحرارة' : 'Temperature';
      case 'humidity':
        return isRTL ? 'الرطوبة' : 'Humidity';
      case 'battery':
        return isRTL ? 'البطارية' : 'Battery';
      case 'rssi':
        return isRTL ? 'قوة الإشارة' : 'Signal Strength';
      default:
        return metric;
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={[styles.loadingText, { color: theme.colors.text }]}>
            {t('detecting_sensor')}
          </Text>
          <Text style={[styles.loadingSubtext, { color: theme.colors.textSecondary }]}>
            {serialNumber}
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  if (error || !result) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <View style={styles.errorContainer}>
          <Icon name="alert-circle" size={64} color={theme.colors.error} />
          <Text style={[styles.errorTitle, { color: theme.colors.text }]}>
            {t('detection_failed')}
          </Text>
          <Text style={[styles.errorMessage, { color: theme.colors.textSecondary }]}>
            {error || t('sensor_not_found')}
          </Text>
          <View style={styles.errorButtons}>
            <TouchableOpacity
              style={[styles.retryButton, { backgroundColor: theme.colors.primary }]}
              onPress={detectSensor}
            >
              <Icon name="refresh" size={20} color="#fff" />
              <Text style={styles.retryButtonText}>{t('try_again')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.cancelButton, { borderColor: theme.colors.border }]}
              onPress={() => navigation.goBack()}
            >
              <Text style={[styles.cancelButtonText, { color: theme.colors.text }]}>
                {t('go_back')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: theme.colors.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Icon
            name={isRTL ? 'chevron-forward' : 'chevron-back'}
            size={28}
            color={theme.colors.text}
          />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: theme.colors.text }]}>
          {t('sensor_detected')}
        </Text>
        <View style={{ width: 40 }} />
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Success Icon */}
        <View style={styles.successIcon}>
          <Icon name="checkmark-circle" size={80} color="#4CAF50" />
        </View>

        {/* Duplicate Warning */}
        {result.isDuplicate && result.existingDevice && (
          <View
            style={[styles.warningCard, { backgroundColor: '#fff3cd', borderColor: '#ffc107' }]}
          >
            <Icon name="warning" size={24} color="#ff9800" />
            <View style={{ flex: 1, marginLeft: 12 }}>
              <Text style={[styles.warningTitle, { color: '#856404' }]}>
                {t('sensor_already_exists')}
              </Text>
              <Text style={[styles.warningText, { color: '#856404' }]}>
                {t('existing_device')}: {result.existingDevice.name}
              </Text>
            </View>
          </View>
        )}

        {/* Gateway Info */}
        <View style={[styles.card, { backgroundColor: theme.colors.card }]}>
          <Text style={[styles.cardTitle, { color: theme.colors.text }]}>
            {t('gateway_information')}
          </Text>
          <View style={styles.infoRow}>
            <Icon name="hardware-chip" size={20} color={theme.colors.primary} />
            <Text style={[styles.infoLabel, { color: theme.colors.textSecondary }]}>
              {t('gateway_name')}:
            </Text>
            <Text style={[styles.infoValue, { color: theme.colors.text }]}>
              {result.gateway.name}
            </Text>
          </View>
          <View style={styles.infoRow}>
            <Icon name="location" size={20} color={theme.colors.primary} />
            <Text style={[styles.infoLabel, { color: theme.colors.textSecondary }]}>
              {t('location')}:
            </Text>
            <Text style={[styles.infoValue, { color: theme.colors.text }]}>
              {result.gateway.siteName}
              {result.gateway.zoneName && ` / ${result.gateway.zoneName}`}
            </Text>
          </View>
          <View style={styles.infoRow}>
            <Icon name="key" size={20} color={theme.colors.primary} />
            <Text style={[styles.infoLabel, { color: theme.colors.textSecondary }]}>
              {t('serial_number')}:
            </Text>
            <Text style={[styles.infoValue, { color: theme.colors.text, fontFamily: 'monospace' }]}>
              {result.gateway.sn}
            </Text>
          </View>
        </View>

        {/* Sensor Info */}
        <View style={[styles.card, { backgroundColor: theme.colors.card }]}>
          <Text style={[styles.cardTitle, { color: theme.colors.text }]}>
            {t('sensor_information')}
          </Text>
          <View style={styles.infoRow}>
            <Text style={[styles.sensorNumber, { color: theme.colors.primary }]}>
              #{result.sensor.sensorNumber}
            </Text>
            <Text style={[styles.suggestedName, { color: theme.colors.text }]}>
              {result.sensor.suggestedName}
            </Text>
          </View>
          <View style={styles.serialBox}>
            <Text style={[styles.serialLabel, { color: theme.colors.textSecondary }]}>
              {t('sensor_serial')}:
            </Text>
            <Text style={[styles.serialValue, { color: theme.colors.text }]}>{serialNumber}</Text>
          </View>
        </View>

        {/* Available Metrics */}
        <View style={[styles.card, { backgroundColor: theme.colors.card }]}>
          <Text style={[styles.cardTitle, { color: theme.colors.text }]}>
            {t('available_metrics')} ({result.availableMetrics.length})
          </Text>
          <View style={styles.metricsGrid}>
            {result.availableMetrics.map((metric) => {
              const dp = result.datapoints[metric];
              return (
                <View
                  key={metric}
                  style={[styles.metricCard, { backgroundColor: theme.colors.background }]}
                >
                  <Text style={styles.metricIcon}>{getMetricIcon(metric)}</Text>
                  <Text style={[styles.metricName, { color: theme.colors.text }]}>
                    {getMetricName(metric)}
                  </Text>
                  {dp && (
                    <>
                      <Text style={[styles.metricValue, { color: theme.colors.primary }]}>
                        {dp.lastValue} {dp.unit}
                      </Text>
                      <Text style={[styles.metricSymbol, { color: theme.colors.textSecondary }]}>
                        {dp.symbol}
                      </Text>
                    </>
                  )}
                </View>
              );
            })}
          </View>
        </View>

        {/* Optional Metrics */}
        {result.optionalMetrics.length > 0 && (
          <View style={[styles.card, { backgroundColor: theme.colors.card }]}>
            <Text style={[styles.cardTitle, { color: theme.colors.text }]}>
              {t('optional_metrics')} ({result.optionalMetrics.length})
            </Text>
            <View style={styles.metricsGrid}>
              {result.optionalMetrics.map((metric) => {
                const dp = result.datapoints[metric];
                return (
                  <View
                    key={metric}
                    style={[
                      styles.metricCard,
                      { backgroundColor: theme.colors.background, opacity: 0.7 },
                    ]}
                  >
                    <Text style={styles.metricIcon}>{getMetricIcon(metric)}</Text>
                    <Text style={[styles.metricName, { color: theme.colors.text }]}>
                      {getMetricName(metric)}
                    </Text>
                    {dp && (
                      <>
                        <Text style={[styles.metricValue, { color: theme.colors.textSecondary }]}>
                          {dp.lastValue} {dp.unit}
                        </Text>
                        <Text style={[styles.metricSymbol, { color: theme.colors.textSecondary }]}>
                          {dp.symbol}
                        </Text>
                      </>
                    )}
                  </View>
                );
              })}
            </View>
          </View>
        )}

        {/* Continue Button */}
        <TouchableOpacity
          style={[
            styles.continueButton,
            { backgroundColor: theme.colors.primary, opacity: result.isDuplicate ? 0.5 : 1 },
          ]}
          onPress={handleContinue}
          disabled={result.isDuplicate}
        >
          <Text style={styles.continueButtonText}>{t('continue_to_configuration')}</Text>
          <Icon name={isRTL ? 'chevron-back' : 'chevron-forward'} size={24} color="#fff" />
        </TouchableOpacity>

        <View style={{ height: 40 }} />
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  content: {
    flex: 1,
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
  },
  loadingSubtext: {
    fontSize: 14,
    marginTop: 8,
    fontFamily: 'monospace',
  },
  successIcon: {
    alignItems: 'center',
    marginVertical: 24,
  },
  warningCard: {
    flexDirection: 'row',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 16,
  },
  warningTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  warningText: {
    fontSize: 14,
    marginTop: 4,
  },
  card: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  cardSubtitle: {
    fontSize: 14,
    marginBottom: 16,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  infoLabel: {
    fontSize: 14,
    marginLeft: 8,
  },
  infoValue: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 8,
    flex: 1,
  },
  sensorNumber: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  suggestedName: {
    fontSize: 18,
    fontWeight: '600',
    marginLeft: 12,
    flex: 1,
  },
  serialBox: {
    backgroundColor: 'rgba(0,0,0,0.05)',
    padding: 12,
    borderRadius: 8,
    marginTop: 8,
  },
  serialLabel: {
    fontSize: 12,
    marginBottom: 4,
  },
  serialValue: {
    fontSize: 16,
    fontFamily: 'monospace',
    fontWeight: '600',
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 8,
  },
  metricCard: {
    width: '48%',
    padding: 12,
    borderRadius: 8,
    marginRight: '2%',
    marginBottom: 8,
    alignItems: 'center',
  },
  metricIcon: {
    fontSize: 32,
    marginBottom: 8,
  },
  metricName: {
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },
  metricValue: {
    fontSize: 16,
    fontWeight: 'bold',
    marginTop: 4,
  },
  metricSymbol: {
    fontSize: 12,
    marginTop: 2,
  },
  continueButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 18,
    borderRadius: 12,
    marginTop: 16,
  },
  continueButtonText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#fff',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginTop: 16,
    textAlign: 'center',
  },
  errorMessage: {
    fontSize: 14,
    marginTop: 8,
    textAlign: 'center',
    lineHeight: 20,
  },
  errorButtons: {
    flexDirection: 'row',
    marginTop: 24,
  },
  retryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    marginRight: 12,
  },
  retryButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  cancelButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});
